import { Elements } from "@stripe/react-stripe-js";
import { useMemo } from "react";

import { mergeStripeAppearance } from "@/react/components/Paywalls/StripeElements/mergeStripeAppearance";
import { customizedAppearance } from "@/react/components/Paywalls/StripeElements/stripeElementsOptions";
import { useCompassAppearance } from "@/react/components/Paywalls/StripeElements/useCompassAppearance";
import { loadStripeWrapper } from "@/react/hooks/paywalls/useStripeElement";
import { paywallPriceTypes } from "@/react/types/PaywallPrice";

import { getStripeElementsMode } from "./helpers/getStripeElementsMode";
import { PaywallCheckoutAttemptsContextProvider } from "./PaywallCheckoutAttemptsContextProvider";
import type {
  BasePaywallCheckoutAttemptsContextProviderProps,
  OnAddressChange,
  OnCheckoutTermsChanged,
  OnComplete,
  OnError,
  OnPaymentMethodChange,
  OnProgress,
  PaywallCheckoutAttemptsMode,
} from "./types";
import { paywallCheckoutAttemptsModes } from "./types";

/**
 * PaywallCheckoutAttemptsProvider
 *
 * A self-contained provider that encapsulates the complete Stripe payment processing flow
 * for paywall checkout attempts. Designed for reuse across main checkout and upsell pages.
 *
 * ## Architecture
 *
 * The provider is structured in three layers:
 *
 * 1. **PaywallCheckoutAttemptsProvider** (this component)
 *    - Public API for consumers
 *    - Initializes Stripe.js and configures the `<Elements>` provider
 *    - Determines payment vs setup mode based on order amount
 *
 * 2. **PaywallCheckoutAttemptsContextProvider** (internal)
 *    - Manages internal state (stage, payment method, address)
 *    - Orchestrates submission and verification flows
 *    - Syncs state changes back to consumers via callbacks
 *
 * 3. **Specialized Hooks** (internal)
 *    - `useSubmitAttempt`: Handles the submission flow (setup → create → confirm)
 *    - `useVerifyAttempt`: Handles URL-based verification after redirects
 *    - `useConfirmPayment`: Manages Stripe payment confirmation
 *
 * ## Usage
 *
 * ```tsx
 * <PaywallCheckoutAttemptsProvider
 *   // Community/processor configuration
 *   communityId={...}
 *   communityProcessorId={...}
 *   communityProcessorKey={...}
 *   communityProcessorPaymentMethodConfigurationId={...}
 *
 *   // Member information
 *   memberId={...}
 *   memberEmail={...}
 *   doesMemberHasDefaultPaymentMethod={...}
 *
 *   // Order details
 *   orderAmount={...}
 *   orderCurrency={...}
 *   paywallPriceId={...}
 *
 *   // Requirements (consumer determines these based on business logic)
 *   isMemberAddressRequired={...}
 *   isPaymentMethodRequired={...}
 *
 *   // Lifecycle callbacks
 *   onProgress={(stage) => { ... }}
 *   onComplete={({ attemptId, attemptStatus, paymentMethodType }) => { ... }}
 *   onError={({ error }) => { ... }}
 *
 *   // Optional: sync internal state changes back to consumer
 *   onAddressChange={(address) => { ... }}
 *   onPaymentMethodChange={({ isComplete, paymentMethodType }) => { ... }}
 * >
 *   {children}
 * </PaywallCheckoutAttemptsProvider>
 * ```
 *
 * ## Consumer Components
 *
 * Inside the provider, use these components to render Stripe Elements:
 * - `<PaymentElement />` - Renders Stripe's Payment Element
 * - `<AddressElement />` - Renders Stripe's Address Element
 *
 * To trigger submission, access the context:
 * ```tsx
 * const { submitAttempt, stage } = usePaywallCheckoutAttemptsContext();
 * ```
 *
 * ## Stage Flow
 *
 * The provider manages a stage-based state machine:
 * ```
 * idle → settingUpPaymentMethod → creatingAttempt → confirmingAttempt → [redirect] → idle → verifyingAttempt → completed
 *                                                                                                            ↘ failed
 * ```
 *
 * **Important**: All payment methods trigger a redirect for verification, regardless of
 * whether the payment method itself requires a redirect (e.g., Bancontact, iDEAL) or not
 * (e.g., cards). This standardized "redirect-all" pattern ensures consistent verification
 * handling: after `confirmingAttempt`, the user is always redirected back to the checkout
 * page with URL parameters (`invoice`, `payment_intent`, `payment_method_type`) that
 * trigger verification on page load.
 *
 * ## Design Principles
 *
 * 1. **Self-Contained**: All Stripe logic is encapsulated; consumers don't interact
 *    with Stripe directly.
 *
 * 2. **Consumer Computes Business Logic**: The provider doesn't know about trials,
 *    pricing tiers, or domain concepts. Consumers compute `isPaymentMethodRequired`
 *    and `isMemberAddressRequired` and pass them as props.
 *
 * 3. **Callback-Based Integration**: For legacy system integration, optional callbacks
 *    (`onAddressChange`, `onPaymentMethodChange`) allow syncing internal state back
 *    to external form/context state.
 */
export interface PaywallCheckoutAttemptsProviderProps extends BasePaywallCheckoutAttemptsContextProviderProps {
  isMemberAddressRequired: boolean;
  isPaymentMethodRequired: boolean;
  /**
   * Checkout mode. Defaults to "default".
   * - "default": Standard checkout flow with payment and address forms.
   * - "express": Express checkout using saved payment method only (for upsells).
   */
  mode?: PaywallCheckoutAttemptsMode;
  onAddressChange?: OnAddressChange;
  onCheckoutTermsChanged?: OnCheckoutTermsChanged;
  onComplete: OnComplete;
  onError: OnError;
  onPaymentMethodChange?: OnPaymentMethodChange;
  onProgress: OnProgress;
}

export const PaywallCheckoutAttemptsProvider = ({
  children,
  mode = paywallCheckoutAttemptsModes.default,
  ...props
}: PaywallCheckoutAttemptsProviderProps) => {
  const {
    communityProcessorId,
    communityProcessorKey,
    communityProcessorPaymentMethodConfigurationId,
    memberProcessorCustomerSessionClientSecret,
    orderAmount,
    orderCurrency,
    orderCurrencyMinimumChargeAmount,
    orderPriceType,
  } = props;

  // Draft paywalls are previewed before Stripe is connected, so there may be no key.
  const stripe = useMemo(
    () =>
      communityProcessorKey
        ? loadStripeWrapper({
            stripeAccountId: communityProcessorId,
            stripePublicKey: communityProcessorKey,
            locale: window.locale,
          })
        : null,
    [communityProcessorId, communityProcessorKey],
  );

  const compassAppearance = useCompassAppearance();
  const appearance = useMemo(
    () => mergeStripeAppearance(customizedAppearance, compassAppearance),
    [compassAppearance],
  );

  const isOrderRecurring = orderPriceType !== paywallPriceTypes.onetime;
  const isZeroAmount = orderAmount < orderCurrencyMinimumChargeAmount;
  const effectiveAmount = isZeroAmount ? 0 : orderAmount;
  const stripeElementsMode = getStripeElementsMode(
    isOrderRecurring,
    effectiveAmount,
    orderCurrency,
  );

  const stripeElementsOptions = useMemo(
    () => ({
      amount: effectiveAmount,
      appearance,
      currency: orderCurrency,
      customerSessionClientSecret:
        memberProcessorCustomerSessionClientSecret ?? undefined,
      mode: stripeElementsMode,
      paymentMethodConfiguration:
        communityProcessorPaymentMethodConfigurationId,
    }),
    [
      appearance,
      communityProcessorPaymentMethodConfigurationId,
      effectiveAmount,
      memberProcessorCustomerSessionClientSecret,
      orderCurrency,
      stripeElementsMode,
    ],
  );

  return (
    <Elements stripe={stripe} options={stripeElementsOptions}>
      <PaywallCheckoutAttemptsContextProvider
        {...props}
        isOrderRecurring={isOrderRecurring}
        mode={mode}
        stripeElementsMode={stripeElementsMode}
      >
        {children}
      </PaywallCheckoutAttemptsContextProvider>
    </Elements>
  );
};
